/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, CreateInstallerTokenRequest, CreateInstallerTokenResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type CreateInstallerTokenMutationConfig = {
  onSuccess?: (data: CreateInstallerTokenResponse, variables: CreateInstallerTokenRequest) => void;
  onError?: (error: ApiError, variables: CreateInstallerTokenRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: CreateInstallerTokenRequest) => Promise<unknown> | void;
};

/**
 * Hook for creating new installer token through the management plane API.
 *
 * @param config.onSuccess - Called after successful token creation
 * @param config.onError - Called if token creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for creating new installer token
 *
 * @example
 * ```tsx
 * const { mutate } = useCreateInstallerTokenMutation({
 *   onSuccess: (data) => console.log('Token Created:', data),
 *   onError: (error) => console.error('Token Creation failed:', error)
 * });
 *
 * // Create Token
 * mutate({ name: 'Production Server' });
 * ```
 */

export function useCreateInstallerTokenMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: CreateInstallerTokenMutationConfig) {
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const { mutateAsync, mutate, isError, error } = useMutation<
    CreateInstallerTokenResponse,
    ApiError,
    CreateInstallerTokenRequest
  >({
    mutationFn: (newToken: CreateInstallerTokenRequest) => {
      return mgmtPlaneAPIClient.installerTokens.createInstallerToken({
        tenantId: tenantID!,
        requestBody: {
          ...newToken,
          subtenantId: selectedSubtenantID,
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, mutate, isError, error };
}
